import { useState, useEffect } from "react";
import axios from "axios";
import { IModuleEntity, IRolePerrmission } from "../controls/RelationAccesses/AccessDomainModels";
import { getAntiForgeryToken } from "../utils/Fetch";
import { IEmployeeEntity } from "../controls/Relations/RequestControl";

export interface IDocumentHtmlReplyJsonData {
    html: string,
    title: string,
    acceptanceCheckbox: string,
    maxWidthDialog: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false,
    fullScreenDialog: boolean | null,
}

export interface INDAAccessRelationJsonData
{
    employee: IEmployeeEntity;
    resource: IModuleEntity;
    role?: IRolePerrmission;
    variables?: { [key: string]: string };

    from: string;
    to: string;

    login: string;
    environment: string;
    personAccountDescription: string;
    personAccountType: string;
    company: string;
    referenceNumber: string;
    systemName: string;
    telephone: string;
    email: string;
    location: string;

    description: string;

    excluded: string[];
}

interface INDARequestJsonData {
    issueId: number | null,
    templateName: string,
    relation: INDAAccessRelationJsonData,
}

type GetNDAHtmlApi = [(m: INDARequestJsonData) => void, IDocumentHtmlReplyJsonData, boolean, boolean];
export const useGetNDAHtmlApi: () => GetNDAHtmlApi = () => {
    const [model, setModel] = useState<INDARequestJsonData>(null);
    const [result, setResult] = useState<IDocumentHtmlReplyJsonData>(null);

    const [isLoading, setIsLoading] = useState(false);
    const [isError, setIsError] = useState(false);
    useEffect(() => {
        const fetchData = async () => {
            setIsError(false);
            setIsLoading(true);
            setResult(null);
            try {
                const result = await axios.post('/Documents/GetNDAHtml/', model, {
                    headers: {
                        __RequestVerificationToken: getAntiForgeryToken(),
                    }
                });
                setResult(result.data);
            } catch (error) {
                console.error(error);
                setIsError(true);
            }
            setIsLoading(false);
        };

        if (!model)
            return;

        fetchData();
    }, [model]);

    return [setModel, result, isLoading, isError];
}

interface IDecisionNDARequestJsonData {
    templateName: string,
    relationId: string,
    issueId: number,
}

type GetDecisionNDAHtmlApi = [(m: IDecisionNDARequestJsonData) => void, IDocumentHtmlReplyJsonData, boolean, boolean];
export const useGetDecisionNDAHtmlApi: () => GetDecisionNDAHtmlApi = () => {
    const [model, setModel] = useState<IDecisionNDARequestJsonData>(null);
    const [result, setResult] = useState<IDocumentHtmlReplyJsonData>(null);

    const [isLoading, setIsLoading] = useState(false);
    const [isError, setIsError] = useState(false);
    useEffect(() => {
        const fetchData = async () => {
            setIsError(false);
            setIsLoading(true);
            setResult(null);
            try {
                const result = await axios.post('/Documents/GetDecisionNDAHtml/', model, {
                    headers: {
                        __RequestVerificationToken: getAntiForgeryToken(),
                    }
                });
                setResult(result.data);
            } catch (error) {
                console.error(error);
                setIsError(true);
            }
            setIsLoading(false);
        };

        if (!model)
            return;

        fetchData();
    }, [model]);

    return [setModel, result, isLoading, isError];
}

interface IDecisionNDADetailsRequestJsonData {
    relationId: string,
    issueId: number | string,
}

type GetDecisionNDADetailsApi = [(m: IDecisionNDADetailsRequestJsonData) => void, IDocumentHtmlReplyJsonData, boolean, boolean];
export const useGetDecisionNDADetailsApi: () => GetDecisionNDADetailsApi = () => {
    const [model, setModel] = useState<IDecisionNDADetailsRequestJsonData>(null);
    const [result, setResult] = useState<IDocumentHtmlReplyJsonData>(null);

    const [isLoading, setIsLoading] = useState(false);
    const [isError, setIsError] = useState(false);
    useEffect(() => {
        const fetchData = async () => {
            setIsError(false);
            setIsLoading(true);
            setResult(null);
            try {
                const result = await axios.post('/Documents/GetDecisionNDADetails/', model, {
                    headers: {
                        __RequestVerificationToken: getAntiForgeryToken(),
                    }
                });
                setResult(result.data);
            } catch (error) {
                console.error(error);
                setIsError(true);
            }
            setIsLoading(false);
        };

        if (!model)
            return;

        fetchData();
    }, [model]);

    return [setModel, result, isLoading, isError];
}

interface IGenerateForIssueApiRequest {
    issueId: number,
    templateName: string,
}

type GenerateForIssueApi = [(m: IGenerateForIssueApiRequest) => void, IDocumentHtmlReplyJsonData, boolean, boolean];
export const useGenerateForIssueApi: () => GenerateForIssueApi = () => {
    const [model, setModel] = useState<IGenerateForIssueApiRequest>(null);
    const [result, setResult] = useState<IDocumentHtmlReplyJsonData>(null);

    const [isLoading, setIsLoading] = useState(false);
    const [isError, setIsError] = useState(false);
    useEffect(() => {
        const fetchData = async () => {
            setIsError(false);
            setIsLoading(true);
            setResult(null);
            try {
                const result = await axios.get(`/Documents/GenerateForIssue/?issueId=${model.issueId}&templateName=${encodeURIComponent(model.templateName)}`);
                setResult(result.data);
            } catch (error) {
                console.error(error);
                setIsError(true);
            }
            setIsLoading(false);
        };

        if (!model)
            return;

        fetchData();
    }, [model]);

    return [setModel, result, isLoading, isError];
}